import cn from 'classnames';
import { memo, useCallback, useEffect, useRef } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import { Avatar } from '@app/components/Avatar';
import { getLeadListItemViewModel } from '@app/components/LeadCard/utils/getLeadListItemViewModel';
import { TruncatedHeaderText } from '@app/shared/components';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import './LeadListItem.scss';

interface LeadListItemProps {
  lead: ModelLead;
  canShowContacts: boolean;
  isSelected?: boolean;
  onClick(lead: ModelLead): void;
}

const LeadListItemComponent = ({
  lead,
  canShowContacts,
  isSelected = false,
  onClick,
}: LeadListItemProps) => {
  const { displayName, avatarBase64, lastMessage, time, isUnread, isNoAnswer } =
    getLeadListItemViewModel(lead, canShowContacts);
  const itemRef = useRef<HTMLButtonElement | null>(null);

  const clickHandler = useCallback(() => {
    onClick(lead);
  }, [lead, onClick]);

  // Открытый лид должен быть виден в списке при входе в карточку и при смене
  // выбранного (task/11126): `nearest` не двигает список, если элемент уже в
  // видимой области — клик по соседнему лиду ничего не прокручивает.
  useEffect(() => {
    if (!isSelected) return;

    itemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isSelected]);

  return (
    <button
      ref={itemRef}
      className={cn('LeadListItem', {
        'LeadListItem--selected': isSelected,
        // Голубой фон означает «неотвечено», точка — «непрочитано».
        // Классы исторически названы наоборот, переименование — отдельной задачей.
        'LeadListItem--unread': isNoAnswer,
      })}
      type="button"
      onClick={clickHandler}
      aria-pressed={isSelected}
      aria-label={displayName}
    >
      <Avatar
        className="LeadListItem__avatar"
        src={avatarBase64}
        nameFirst={displayName}
        size={32}
        round
      />

      <span className="LeadListItem__content">
        <span className="LeadListItem__name-row">
          <TruncatedHeaderText
            className="LeadListItem__name"
            lineCount={1}
            text={displayName}
          />
        </span>

        <span className="LeadListItem__last-message">
          <SocialIntegrationIcon
            type={lead.SocialIntegrationType}
            size="sm"
            className="LeadListItem__message-icon"
          />
          <span className="LeadListItem__message-text">{lastMessage}</span>
        </span>
      </span>

      {(isUnread || time) && (
        <span className="LeadListItem__time-group">
          {isUnread && (
            <span className="LeadListItem__no-answer-dot" aria-hidden="true" />
          )}
          {time && <span className="LeadListItem__time">{time}</span>}
        </span>
      )}
    </button>
  );
};

export const LeadListItem = memo(LeadListItemComponent);
